import React from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import {
  Box,
  Container,
  List,
  ListItem,
  Stack,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedListItemText as ListItemText } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { Link as RouterLink } from 'react-router-dom';
import SEO from '../components/SEO';
import KundlistarSchema from '../components/KundlistarSchema';

const ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=com.kundlistar.app';

type LandingPageConfig = {
  path: string;
  title: string;
  description: string;
  keywords: string;
  h1: string;
  intro: string;
  systemTitle: string;
  systemText: string;
  whyTitle: string;
  whyPoints: string[];
  features: string[];
  audience: string[];
  premium: string[];
  faqs: Array<{ question: string; answer: string }>;
  primaryCta?: string;
  webCta?: string;
  webPath?: string;
};

const commonPremium = [
  'Free users can explore core tools and create charts to understand the workflow.',
  'plan users get deeper reports, advanced modules and PDF-friendly analysis where available.',
  'Yearly plan users unlock plan features at Rs. 999/year.',
];

const createPage = (
  path: string,
  title: string,
  h1: string,
  focus: string,
  features: string[],
  audience: string[],
  webPath = '/calculate'
): LandingPageConfig => ({
  path,
  title,
  description: `Use Kundlistar as a ${focus} with structured astrology insights, chart workflows, app access and premium reports at Rs. 999/year.`,
  keywords: `${focus}, Kundlistar, Kundlistar app, astrology app India, kundli app, premium astrology reports`,
  h1,
  intro: `Kundlistar helps Indian astrology users explore ${focus.toLowerCase()} through a simple web and Android experience. It is built for practical guidance, chart study and deeper spiritual perspective without making guaranteed claims.`,
  systemTitle: `What is ${focus}?`,
  systemText: `${focus} helps users study birth details, patterns, timing and astrological indications through a defined calculation or interpretation system. Kundlistar presents this information in a structured way so beginners, students and working astrologers can review insights with more clarity.`,
  whyTitle: `Why use Kundlistar for ${focus}?`,
  whyPoints: [
    'Web and Android access for chart work on desktop or mobile.',
    'Connected astrology modules so users can compare Vedic, KP, Nadi, BNN, Jaimini, numerology and daily utility insights.',
    'Clear report-style presentation designed for learning, client discussions and personal exploration.',
  ],
  features,
  audience,
  premium: commonPremium,
  faqs: [
    {
      question: `Is Kundlistar useful for ${focus}?`,
      answer: `Yes. Kundlistar provides a focused workflow for ${focus.toLowerCase()} along with related astrology modules for wider study and guidance.`,
    },
    {
      question: 'Is astrology guidance a replacement for professional advice?',
      answer: 'No. Kundlistar presents astrology as guidance and spiritual perspective. Users should not treat it as a replacement for professional medical, legal, financial or safety advice.',
    },
    {
      question: 'Can I use Kundlistar on Android?',
      answer: 'Yes. Kundlistar is available on web and as an Android app from Google Play.',
    },
  ],
  primaryCta: 'Download Kundlistar App now',
  webCta: `Explore ${focus} on Web`,
  webPath,
});

export const landingPages: LandingPageConfig[] = [
  createPage(
    '/kundli-app',
    'Best Kundli App for Birth Chart, Reports & Matching | Kundlistar',
    'Kundli App for Birth Chart, Reports and Matching',
    'Kundli app',
    ['Birth chart creation', 'Kundli analysis workflow', 'Matching and compatibility tools', 'Vedic chart layers', 'Premium AI-based astrology reports'],
    ['People exploring their birth chart', 'Astrology students', 'Astrologers who need a mobile-friendly kundli workflow']
  ),
  createPage(
    '/astrology-app',
    'Best Astrology App for Kundli, Panchang & Reports | Kundlistar',
    'Astrology App for Kundli, Panchang and Premium Reports',
    'astrology app',
    ['Kundli and birth chart tools', 'Daily Panchang and Choghadiya', 'KP, Nadi, BNN and Jaimini modules', 'Numerology and Astro Vastu', 'Premium reports and PDF workflows'],
    ['Indian astrology users', 'Spiritual learners', 'Astrologers and content creators']
  ),
  createPage(
    '/astrology-app-for-astrologers',
    'Astrology App for Astrologers with KP, Nadi & Vedic Tools | Kundlistar',
    'Astrology App for Professional Astrologers',
    'astrology app for astrologers',
    ['Saved chart workflow', 'KP cusps and significators', 'Nadi and BNN life event layers', 'Jaimini and Tajika Varshphal support', 'Client-friendly report views'],
    ['Professional astrologers', 'Astrology consultants', 'Jyotish practitioners handling repeated chart analysis']
  ),
  createPage(
    '/astrology-app-for-students',
    'Astrology App for Students Learning Kundli, KP & Nadi | Kundlistar',
    'Astrology App for Students and Serious Learners',
    'astrology app for students',
    ['Beginner-friendly chart access', 'Multiple systems in one platform', 'House, planet and timing study support', 'Numerology and Panchang utilities', 'Premium reports for deeper learning'],
    ['Astrology students', 'Self-learners', 'Users comparing multiple Indian astrology systems']
  ),
  createPage(
    '/ai-astrology-report-app',
    'AI Astrology Report App for Premium Kundli Insights | Kundlistar',
    'AI Astrology Report App for Personalised Kundli Insights',
    'AI astrology report app',
    ['Premium AI-based report generation', 'Kundli-backed interpretation', 'Career, relationship and life-theme guidance wording', 'Download-friendly report flow', 'Safe guidance-focused astrology language'],
    ['Users who want readable reports', 'Astrologers preparing client summaries', 'People exploring premium spiritual guidance'],
    '/pricing'
  ),
  createPage(
    '/vedic-astrology-app',
    'Best Vedic Astrology App for Kundli & Chart Analysis | Kundlistar',
    'Vedic Astrology App for Kundli and Chart Analysis',
    'Vedic astrology app',
    ['Rashi chart study', 'Divisional chart support', 'Dasha-oriented review', 'Planet and house analysis', 'Matching and report workflows'],
    ['Vedic astrology learners', 'Jyotish practitioners', 'Users exploring traditional Indian astrology']
  ),
  createPage(
    '/kp-astrology-app',
    'Best KP Astrology App for Cusps, Sublords & Significators | Kundlistar',
    'KP Astrology App for Cuspal, Sublord and Significator Analysis',
    'KP Astrology app',
    ['KP cusps', 'Star lord, sub lord and sub-sub lord workflow', 'KP significators', 'House-wise analysis', 'Dasha and transit-based study support'],
    ['KP astrologers', 'Horary astrology users', 'Students learning cuspal sub lord analysis']
  ),
  createPage(
    '/nadi-astrology-app',
    'Best Nadi Astrology App for Life Event Insights | Kundlistar',
    'Nadi Astrology App for Life Event and Transit Insights',
    'Nadi astrology app',
    ['Nadi life event analysis', 'Transit trigger review', 'Directional insight workflow', 'Progression-based study', 'BNN-linked interpretation layers'],
    ['Nadi astrology learners', 'Advanced astrologers', 'Users exploring life event indications']
  ),
  createPage(
    '/bnn-astrology-app',
    'Best BNN Astrology App for Bhrigu Nandi Nadi Analysis | Kundlistar',
    'BNN Astrology App for Bhrigu Nandi Nadi Analysis',
    'BNN astrology app',
    ['BNN directional charting', 'Planet group study', 'Combination strings', 'Transit and progression context', 'Nadi-linked event insights'],
    ['BNN practitioners', 'Nadi astrology students', 'Astrologers studying planet combinations']
  ),
  createPage(
    '/jaimini-astrology-app',
    'Best Jaimini Astrology App for Karakas & Jaimini Analysis | Kundlistar',
    'Jaimini Astrology App for Karakas and Chart Analysis',
    'Jaimini astrology app',
    ['Jaimini karaka support', 'Rashi and house-based review', 'Advanced chart interpretation layers', 'Cross-study with Vedic modules', 'Structured analysis layout'],
    ['Jaimini astrology students', 'Advanced Jyotish learners', 'Astrologers comparing Parashari and Jaimini layers']
  ),
  createPage(
    '/lal-kitab-astrology-app',
    'Lal Kitab Astrology App for Chart Insights & Remedies | Kundlistar',
    'Lal Kitab Astrology App for Insights and Safe Remedies',
    'Lal Kitab astrology app',
    ['Lal Kitab chart guidance', 'House and planet indications', 'Conduct-based remedy suggestions', 'Premium Lal Kitab report access', 'Clear disclaimer-focused presentation'],
    ['Users exploring Lal Kitab', 'Astrology learners', 'Astrologers who want a structured remedy reference'],
    '/lal-kitab'
  ),
  createPage(
    '/tajika-varshphal-app',
    'Tajika Varshphal App for Annual Horoscope Insights | Kundlistar',
    'Tajika Varshphal App for Annual Horoscope Analysis',
    'Tajika Varshphal app',
    ['Annual chart workflow', 'Yearly astrological indications', 'Tajika-based report structure', 'Premium yearly insights', 'Connected birth chart context'],
    ['Users planning yearly reflection', 'Vedic astrologers', 'Students learning Varshphal'],
    '/varshphal'
  ),
  createPage(
    '/vedic-numerology-app',
    'Best Vedic Numerology App for Moolank, Bhagyank & Grid Yogas | Kundlistar',
    'Vedic Numerology App for Moolank, Bhagyank and Grid Analysis',
    'Vedic Numerology app',
    ['Moolank and Bhagyank', 'Vedic Numerology Grid', 'Missing number insights', 'Name correction perspective', '12-month yearly numerology energy plan'],
    ['Numerology users', 'Name analysis learners', 'Users exploring yearly number guidance'],
    '/vedic-numerology'
  ),
  createPage(
    '/lo-shu-grid-app',
    'Best Lo Shu Grid App for Missing Numbers & Remedies | Kundlistar',
    'Lo Shu Grid App for Missing Numbers and Number Patterns',
    'Lo Shu Grid app',
    ['Lo Shu Grid creation', 'Missing number review', 'Repeated number patterns', 'Number balance insights', 'Premium numerology guidance'],
    ['Lo Shu numerology learners', 'Users exploring date-of-birth patterns', 'Numerologists preparing quick grid reviews'],
    '/lo-shu-grid-numerology'
  ),
  createPage(
    '/name-numerology-calculator',
    'Name Numerology Calculator for Name Number Insights | Kundlistar',
    'Name Numerology Calculator for Name Number Insights',
    'name numerology calculator',
    ['Name number study', 'Spelling-based number perspective', 'Lucky number context', 'Personal numerology report flow', 'Premium name correction guidance wording'],
    ['Users choosing a name spelling', 'Numerology learners', 'Creators and professionals exploring brand or personal names'],
    '/vedic-numerology'
  ),
  createPage(
    '/numerology-grid-calculator',
    'Numerology Grid Calculator for Vedic & Lo Shu Analysis | Kundlistar',
    'Numerology Grid Calculator for Birth Date Patterns',
    'numerology grid calculator',
    ['Birth date grid analysis', 'Missing and repeated number review', 'Vedic and Lo Shu perspectives', 'Personal pattern summary', 'Premium numerology reports'],
    ['Numerology beginners', 'Students comparing grids', 'Users exploring date-of-birth insights'],
    '/vedic-numerology'
  ),
  createPage(
    '/numerology-yearly-plan',
    'Numerology Yearly Plan for 12-Month Energy Insights | Kundlistar',
    'Numerology Yearly Plan for 12-Month Energy Guidance',
    'numerology yearly plan',
    ['Personal year number', '12-month energy overview', 'Month-wise focus areas', 'Premium yearly numerology report', 'Guidance-focused interpretation'],
    ['Users planning the year', 'Numerology learners', 'People who want month-wise spiritual reflection'],
    '/vedic-numerology'
  ),
  createPage(
    '/panchang-app',
    'Best Panchang App for Tithi, Nakshatra & Daily Muhurat | Kundlistar',
    'Panchang App for Tithi, Nakshatra and Daily Muhurat',
    'Panchang app',
    ['Tithi and Nakshatra', 'Yoga and Karana', 'Sunrise and daily timing context', 'Daily Muhurat references', 'Spiritual planning support'],
    ['Daily Panchang users', 'Puja and ritual planners', 'Astrology students'],
    '/panchang'
  ),
  createPage(
    '/choghadiya-app',
    'Best Choghadiya App for Day & Night Muhurat | Kundlistar',
    'Choghadiya App for Day and Night Muhurat Guidance',
    'Choghadiya app',
    ['Day Choghadiya', 'Night Choghadiya', 'Location-based timing context', 'Simple good-time reference', 'Daily utility access on web and Android'],
    ['Users checking daily timing', 'Business and travel planners', 'People following traditional muhurat guidance'],
    '/choghadiya'
  ),
  createPage(
    '/panch-pakshi-app',
    'Panch Pakshi App for Traditional Time Guidance | Kundlistar',
    'Panch Pakshi App for Traditional Timing Insights',
    'Panch Pakshi app',
    ['Panch Pakshi timing view', 'Activity-based indication support', 'Daily spiritual timing context', 'Connected Panchang utilities', 'Mobile-friendly access'],
    ['Traditional astrology users', 'Timing-focused learners', 'Users exploring South Indian spiritual timing methods'],
    '/panch-pakshi'
  ),
  createPage(
    '/astro-vastu-app',
    'Astro Vastu App for Directional Insights & Home Guidance | Kundlistar',
    'Astro Vastu App for Directional and Home Guidance',
    'Astro Vastu app',
    ['Astro Vastu report workflow', 'Direction and zone insights', 'Chart-linked guidance perspective', 'Home and space analysis', 'Premium Vastu report access'],
    ['Homeowners exploring Vastu', 'Astro Vastu learners', 'Consultants needing a structured Vastu view'],
    '/dashboard'
  ),
  createPage(
    '/watch-recommendation-app',
    'Watch Recommendation App by Astrology & Birth Date | Kundlistar',
    'Watch Recommendation App for Astrology-Based Watch Guidance',
    'Watch Recommendation app',
    ['Birth-date based watch guidance', 'Goal-based dial colour suggestions', 'Movement, case shape and strap recommendations', 'Current watch analysis score', 'Brand-free guidance with safe astrology wording'],
    ['Users choosing a new watch', 'Astrology and numerology learners', 'People checking whether their current watch feels supportive'],
    '/watch-recommendation'
  ),
  createPage(
    '/kundli-chart-calculator',
    'Kundli Chart Calculator for Accurate Birth Chart Creation | Kundlistar',
    'Kundli Chart Calculator for Birth Chart Creation',
    'Kundli chart calculator',
    ['Birth detail entry', 'Kundli chart generation', 'Planet and house context', 'Connected report modules', 'Web and Android chart access'],
    ['Anyone creating a kundli', 'Astrology students', 'Astrologers starting a new analysis']
  ),
  createPage(
    '/birth-chart-calculator',
    'Birth Chart Calculator for Vedic Astrology | Kundlistar',
    'Birth Chart Calculator for Vedic Astrology Insights',
    'birth chart calculator',
    ['Vedic birth chart creation', 'Planet placement review', 'House-based study', 'Divisional chart pathway', 'Premium interpretation options'],
    ['Birth chart beginners', 'Jyotish students', 'Users exploring self-understanding through astrology']
  ),
  createPage(
    '/navamsha-chart-calculator',
    'Navamsha Chart Calculator for D9 Vedic Analysis | Kundlistar',
    'Navamsha Chart Calculator for D9 Analysis',
    'Navamsha chart calculator',
    ['D9 Navamsha chart access', 'Marriage and dharma-oriented study context', 'Divisional chart workflow', 'Vedic chart comparison', 'Premium report support'],
    ['Vedic astrology learners', 'Marriage compatibility students', 'Astrologers reviewing divisional charts']
  ),
  createPage(
    '/dashamsha-chart-calculator',
    'Dashamsha Chart Calculator for D10 Career Analysis | Kundlistar',
    'Dashamsha Chart Calculator for D10 Career Indications',
    'Dashamsha chart calculator',
    ['D10 Dashamsha chart access', 'Career and profession-related chart study', 'Divisional chart workflow', 'Planet strength context', 'Premium interpretation options'],
    ['Career astrology learners', 'Vedic astrologers', 'Users exploring professional life indications']
  ),
  createPage(
    '/vimshottari-dasha-calculator',
    'Vimshottari Dasha Calculator for Mahadasha & Antardasha | Kundlistar',
    'Vimshottari Dasha Calculator for Timing Insights',
    'Vimshottari Dasha calculator',
    ['Mahadasha and Antardasha context', 'Timing-oriented astrology study', 'Chart-linked dasha interpretation', 'Transit and report workflow', 'Premium guidance access'],
    ['Vedic timing students', 'Astrologers studying periods', 'Users exploring current life phase indications']
  ),
  createPage(
    '/kundli-matching-app',
    'Kundli Matching App for Compatibility & Guna Milan | Kundlistar',
    'Kundli Matching App for Compatibility and Guna Milan',
    'Kundli matching app',
    ['Kundli matching workflow', 'Compatibility scoring context', 'Guna Milan support', 'Nadi, Bhakoot and Mangal Dosha references', 'Guidance-focused matching report'],
    ['Couples and families exploring compatibility', 'Astrologers preparing matching reports', 'Users studying relationship indications'],
    '/matching'
  ),
];

export const seoLandingPagePaths = landingPages.map((page) => page.path);

const LandingPage: React.FC<{ page: LandingPageConfig }> = ({ page }) => (
  <>
    <SEO
      title={page.title}
      description={page.description}
      path={page.path}
      image="/logo512.png"
      keywords={page.keywords}
    />
    <KundlistarSchema />

    <Box
      sx={{
        color: '#ffffff',
        minHeight: { xs: 560, md: 620 },
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(90deg, rgba(9, 36, 24, 0.98), rgba(16, 78, 50, 0.9))',
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 9 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
            gap: 5,
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="h2" component="h1" sx={{ fontWeight: 600, mb: 2, color: '#ffffff' }}>
              {page.h1}
            </Typography>
            <Typography variant="h6" sx={{ lineHeight: 1.8, color: 'rgba(255,255,255,0.88)', maxWidth: 760 }}>
              {page.intro}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
              <Button
                href={ANDROID_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                color="secondary"
                startIcon={<DownloadIcon />}
                endIcon={<OpenInNewIcon />}
              >
                {page.primaryCta}
              </Button>
              <Button
                component={RouterLink}
                to={page.webPath || '/calculate'}
                variant="outlined"
                sx={{ color: '#ffffff', borderColor: 'rgba(255,255,255,0.7)' }}
              >
                {page.webCta || 'Use Kundlistar on Web'}
              </Button>
            </Stack>
          </Box>
          <Box>
            <Box
              component="img"
              src="/logo512.png"
              alt="Kundlistar app preview"
              sx={{
                width: '100%',
                maxWidth: 340,
                display: 'block',
                mx: 'auto',
                filter: 'drop-shadow(0 24px 50px rgba(0,0,0,0.36))',
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>

    <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 5,
        }}
      >
        <Box>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 850, mb: 2 }}>
            {page.systemTitle}
          </Typography>
          <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
            {page.systemText}
          </Typography>
        </Box>
        <Box>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 850, mb: 2 }}>
            {page.whyTitle}
          </Typography>
          <List dense>
            {page.whyPoints.map((point) => (
              <ListItem key={point} disableGutters>
                <ListItemText primary={point} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
    </Container>

    <Box sx={{ bgcolor: "var(--app-surface-neutral, #f7fbf8)", py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 5,
          }}
        >
          <Box>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 850, mb: 2 }}>
              Features inside Kundlistar
            </Typography>
            <List dense>
              {page.features.map((feature) => (
                <ListItem key={feature} disableGutters>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Box>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 850, mb: 2 }}>
              Useful for
            </Typography>
            <List dense>
              {page.audience.map((item) => (
                <ListItem key={item} disableGutters>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Box>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 850, mb: 2 }}>
              Free and premium benefits
            </Typography>
            <List dense>
              {page.premium.map((item) => (
                <ListItem key={item} disableGutters>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Container>
    </Box>

    <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '0.8fr 1.2fr' },
          gap: 5,
          alignItems: 'center',
        }}
      >
        <Box>
          <Box
            component="img"
            src="/logo512.png"
            alt="Kundlistar Studio"
            sx={{ width: '100%', maxWidth: 260, mx: 'auto', display: 'block', filter: 'drop-shadow(0 24px 50px rgba(25, 84, 54, 0.18))' }}
          />
        </Box>
        <Box>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 850, mb: 2 }}>
            App preview and launch offer
          </Typography>
          <Typography color="text.secondary" sx={{ lineHeight: 1.8, mb: 3 }}>
            Kundlistar brings kundli, reports, matching, Panchang, numerology, KP, Nadi, BNN, Jaimini,
            Lal Kitaab, Astro Vastu and AI-based premium astrology reports into one platform.
            Yearly users can unlock plan features at Rs. 999/year.
          </Typography>
          <Button
            href={ANDROID_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            startIcon={<WorkspacePremiumIcon />}
            endIcon={<OpenInNewIcon />}
          >
            Unlock premium reports at Rs. 999/year
          </Button>
        </Box>
      </Box>
    </Container>

    <Box sx={{ bgcolor: '#0b2f20', color: '#ffffff', py: { xs: 6, md: 8 } }}>
      <Container maxWidth="md">
        <Typography variant="h4" component="h2" sx={{ fontWeight: 850, mb: 3, color: '#ffffff' }}>
          FAQs
        </Typography>
        <Stack spacing={3}>
          {page.faqs.map((faq) => (
            <Box key={faq.question}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: '#ffffff' }}>
                {faq.question}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.75, mt: 0.75 }}>
                {faq.answer}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Container>
    </Box>
  </>
);

export const SeoLandingPage: React.FC<{ path: string }> = ({ path }) => {
  const page = landingPages.find((item) => item.path === path);

  if (!page) {
    return null;
  }

  return <LandingPage page={page} />;
};

export default SeoLandingPage;
